import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, ArrowLeft } from "lucide-react";
import { api } from "../../lib/api";
import { leadAttribution } from "../../lib/attribution";
import { Section, QuoteList } from "./ui";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export function PlanningQuoteStep({ postcode, planningRef, setPlanningRef, settings, onProceed, onBack }) {
  const [phase, setPhase] = useState("upload"); // upload | questions | result
  const [hasFull, setHasFull] = useState(null);
  const [storeys, setStoreys] = useState(null);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [mobile, setMobile] = useState("");
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [q, setQ] = useState(null); // quote result

  const [ans, setAns] = useState({
    floor_finish: "laminate", bathrooms: 0, new_soil_pipe: "no",
    kitchen_install: "no", knock_through: "no", underfloor_heating: "no",
    demolition_required: "no", skylights: 0, house_protection: "standard",
    roof_finish: "felt", notes: "",
  });
  const setA = (k, v) => setAns(p => ({ ...p, [k]: v }));

  const Pill = ({ active, onClick, children, testid }) => (
    <button data-testid={testid} onClick={onClick}
      className={`px-4 py-2 text-xs font-bold uppercase tracking-wider border ${active ? "bg-[#12263A] text-white border-[#12263A]" : "border-[#E5E7EB] bg-white hover:border-[#12263A]"}`}>{children}</button>
  );
  const Row = ({ label, hint, children }) => (
    <div className="border-b border-[#E5E7EB] py-4">
      <div className="flex items-center justify-between gap-4">
        <div className="text-sm md:text-base">{label}{hint && <span className="block text-xs text-[#6B7280] mt-0.5">{hint}</span>}</div>
        <div className="flex gap-2 flex-wrap justify-end">{children}</div>
      </div>
    </div>
  );

  const analyze = async () => {
    if (hasFull === null) { toast.error("Let us know if you have a full set of plans."); return; }
    if (storeys === null) { toast.error("Tell us if it's a single or double storey extension."); return; }
    if (!files.length) { toast.error("Upload at least one plan file."); return; }
    if (!name.trim() || !email.trim() || !mobile.trim()) { toast.error("Your name, email and mobile number are required."); return; }
    setLoading(true);
    try {
      const fd = new FormData();
      files.forEach(f => fd.append("files", f));
      fd.append("email", email);
      fd.append("postcode", postcode);
      fd.append("storeys", storeys);
      fd.append("has_full_plans", hasFull ? "true" : "false");
      const r = await api.post("/quote/planning-analyze", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setAnalysis(r.data.analysis || {});
      // pre-fill answers we can infer
      setAns(p => ({
        ...p,
        knock_through: r.data.analysis?.knock_through ? "yes" : p.knock_through,
        skylights: r.data.analysis?.skylights_count || p.skylights,
      }));
      toast.success("Plans read. A few quick questions for an accurate price.");
      setPhase("questions");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) { toast.error(e?.response?.data?.detail || "Analysis failed"); }
    setLoading(false);
  };

  const getQuote = async () => {
    setLoading(true);
    try {
      const r = await api.post("/quote/planning-quote", {
        analysis, answers: { ...ans, storeys }, has_full_plans: hasFull,
        email, name, mobile, postcode, storeys, origin: window.location.origin,
        ...leadAttribution(),
      });
      setQ(r.data);
      toast.success("Your quote is ready — we've emailed you a copy.");
      setPhase("result");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not generate quote"); }
    setLoading(false);
  };

  // ---------- UPLOAD ----------
  if (phase === "upload") {
    return (
      <Section title="Upload your plans for an AI quote" eyebrow="Step 3 · Planning">
        <div className="max-w-2xl space-y-8">
          <div className="border-l-2 border-[#C79A3B] pl-4 text-sm text-[#6B7280]" data-testid="planning-notice">
            Please upload plans for <b className="text-[#12263A]">extensions and ground-floor internal modifications only</b> — not loft conversions, garage conversions or new builds. If your project is one of those, <Link to="/contact" className="underline text-[#12263A] font-bold" data-testid="planning-notice-enquiry-link">send us an enquiry</Link> and we'll be happy to quote for those works.
          </div>

          <div>
            <div className="label-eyebrow mb-3">Is this a single or double storey extension?</div>
            <div className="flex gap-2">
              <Pill testid="plan-storeys-single" active={storeys === "single"} onClick={() => setStoreys("single")}>Single storey</Pill>
              <Pill testid="plan-storeys-double" active={storeys === "double"} onClick={() => setStoreys("double")}>Double storey</Pill>
            </div>
          </div>

          <div>
            <div className="label-eyebrow mb-3">Do you have a full set of plans?</div>
            <p className="text-sm text-[#6B7280] mb-3">A full set means construction / Building Control drawings (plans, elevations &amp; sections with dimensions).</p>
            <div className="flex gap-2">
              <Pill testid="plan-hasfull-yes" active={hasFull === true} onClick={() => setHasFull(true)}>Yes, I have full plans</Pill>
              <Pill testid="plan-hasfull-no" active={hasFull === false} onClick={() => setHasFull(false)}>No / not sure</Pill>
            </div>
            {hasFull === false && (
              <div className="mt-4 border-l-2 border-[#C79A3B] pl-4 text-sm text-[#6B7280]" data-testid="plan-fee-note">
                No problem — we'll produce the full construction &amp; Building Control drawings for you as part of your project.
              </div>
            )}
          </div>

          <div className="de-card p-6">
            <div className="label-eyebrow mb-2">Your name</div>
            <input data-testid="planning-name" value={name} onChange={e => setName(e.target.value)} placeholder="Jane Smith" className="w-full border border-[#E5E7EB] bg-white px-4 py-3 mb-4 text-sm" />
            <div className="label-eyebrow mb-2">Your email</div>
            <input type="email" data-testid="planning-email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@example.com" className="w-full border border-[#E5E7EB] bg-white px-4 py-3 mb-4 text-sm" />
            <div className="label-eyebrow mb-2">Your mobile number</div>
            <input type="tel" data-testid="planning-mobile" value={mobile} onChange={e => setMobile(e.target.value)} placeholder="07xxx xxxxxx" className="w-full border border-[#E5E7EB] bg-white px-4 py-3 mb-4 text-sm" />
            <div className="label-eyebrow mb-2">Planning reference (optional)</div>
            <input data-testid="planning-ref-input" value={planningRef} onChange={e => setPlanningRef(e.target.value)} placeholder="e.g. 24/00123/HOUSE" className="w-full border border-[#E5E7EB] bg-white px-4 py-3 mb-4 text-sm" />
            <div className="label-eyebrow mb-2">Upload plans (images or PDF)</div>
            <label data-testid="planning-upload-label" className="de-btn-secondary cursor-pointer inline-flex">
              {files.length ? `${files.length} file${files.length > 1 ? "s" : ""} selected` : "Choose files"}
              <input type="file" multiple accept="image/*,application/pdf" className="hidden" data-testid="planning-file-input" onChange={e => setFiles(Array.from(e.target.files || []))} />
            </label>
            {files.length > 0 && <ul className="mt-3 text-xs text-[#6B7280] space-y-1">{files.map((f) => <li key={`${f.name}-${f.size}-${f.lastModified}`}>· {f.name}</li>)}</ul>}
          </div>

          <div className="flex gap-3">
            <button data-testid="planning-back" className="de-btn-secondary" onClick={onBack}><ArrowLeft size={16}/> Back</button>
            <button data-testid="planning-analyze-btn" className="de-btn-primary" disabled={loading} onClick={analyze}>
              {loading ? "Reading your plans…" : "Continue"} <ArrowRight size={16}/>
            </button>
          </div>
          {loading && <p className="text-xs text-[#6B7280]">Our AI is reading your drawings — this can take up to a minute.</p>}
        </div>
      </Section>
    );
  }

  // ---------- QUESTIONS ----------
  if (phase === "questions") {
    return (
      <Section title="A few details for an accurate price" eyebrow="Step 3 · Planning">
        <p className="text-[#6B7280] max-w-2xl mb-6">We've read your plans{analysis?.estimator_summary ? ` — ${analysis.estimator_summary}` : ""}. Answer these so your fixed quote is as accurate as possible.</p>
        <div className="max-w-2xl" data-testid="planning-questions">
          <Row label="Floor finish" hint="Carpet is supplied & fitted by a third party (excluded).">
            {[["laminate","Laminate"],["lvt","LVT"],["tiles","Tiles"],["engineered","Engineered wood"],["carpet","Carpet"]].map(([v,l])=>(
              <Pill key={v} testid={`q-floor-${v}`} active={ans.floor_finish===v} onClick={()=>setA("floor_finish",v)}>{l}</Pill>
            ))}
          </Row>
          <Row label="How many bathrooms / WCs in the extension?">
            {[0,1,2,3].map(n=>(<Pill key={n} testid={`q-bath-${n}`} active={ans.bathrooms===n} onClick={()=>setA("bathrooms",n)}>{n}</Pill>))}
          </Row>
          <Row label="New soil pipe / SVP run required?" hint="Needed if adding a WC/bathroom away from existing drainage.">
            <Pill testid="q-soil-yes" active={ans.new_soil_pipe==="yes"} onClick={()=>setA("new_soil_pipe","yes")}>Yes</Pill>
            <Pill testid="q-soil-no" active={ans.new_soil_pipe==="no"} onClick={()=>setA("new_soil_pipe","no")}>No</Pill>
          </Row>
          <Row label="Knock through to the existing house?" hint="Removing the rear wall with a structural steel beam.">
            <Pill testid="q-knock-yes" active={ans.knock_through==="yes"} onClick={()=>setA("knock_through","yes")}>Yes</Pill>
            <Pill testid="q-knock-no" active={ans.knock_through==="no"} onClick={()=>setA("knock_through","no")}>No</Pill>
          </Row>
          <Row label="Kitchen installation?" hint="Units & appliances are supplied by you (excluded).">
            <Pill testid="q-kitchen-yes" active={ans.kitchen_install==="yes"} onClick={()=>setA("kitchen_install","yes")}>Yes, fit it</Pill>
            <Pill testid="q-kitchen-no" active={ans.kitchen_install==="no"} onClick={()=>setA("kitchen_install","no")}>No</Pill>
          </Row>
          <Row label="Underfloor heating?">
            <Pill testid="q-ufh-yes" active={ans.underfloor_heating==="yes"} onClick={()=>setA("underfloor_heating","yes")}>Yes</Pill>
            <Pill testid="q-ufh-no" active={ans.underfloor_heating==="no"} onClick={()=>setA("underfloor_heating","no")}>No</Pill>
          </Row>
          <Row label="Any demolition of existing structure?" hint="e.g. removing an old conservatory or outbuilding.">
            <Pill testid="q-demo-yes" active={ans.demolition_required==="yes"} onClick={()=>setA("demolition_required","yes")}>Yes</Pill>
            <Pill testid="q-demo-no" active={ans.demolition_required==="no"} onClick={()=>setA("demolition_required","no")}>No</Pill>
          </Row>
          <Row label="Number of rooflights / skylights">
            {[0,1,2,3].map(n=>(<Pill key={n} testid={`q-sky-${n}`} active={ans.skylights===n} onClick={()=>setA("skylights",n)}>{n}</Pill>))}
          </Row>
          <Row label="Roof finish" hint="Flat-roof finish for your extension.">
            <Pill testid="q-roof-felt" active={ans.roof_finish==="felt"} onClick={()=>setA("roof_finish","felt")}>3-layer felt (preferred)</Pill>
            <Pill testid="q-roof-grp" active={ans.roof_finish==="grp"} onClick={()=>setA("roof_finish","grp")}>GRP</Pill>
          </Row>
          <Row label="Protection of your existing home" hint="Extensive = additional floor/wall protection & dust screens throughout.">
            <Pill testid="q-protect-standard" active={ans.house_protection==="standard"} onClick={()=>setA("house_protection","standard")}>Standard</Pill>
            <Pill testid="q-protect-extensive" active={ans.house_protection==="extensive"} onClick={()=>setA("house_protection","extensive")}>Extensive</Pill>
          </Row>
          <div className="py-4">
            <div className="label-eyebrow mb-2">Anything else we should know? (optional)</div>
            <textarea data-testid="q-notes" value={ans.notes} onChange={e=>setA("notes",e.target.value)} rows={3} className="w-full border border-[#E5E7EB] bg-white px-4 py-3 text-sm" placeholder="Access, materials you've specified, timescales…" />
          </div>

          <div className="flex gap-3 mt-6">
            <button data-testid="planning-questions-back" className="de-btn-secondary" onClick={()=>setPhase("upload")}><ArrowLeft size={16}/> Back</button>
            <button data-testid="planning-getquote-btn" className="de-btn-primary" disabled={loading} onClick={getQuote}>
              {loading ? "Pricing your build…" : "Generate my quote"} <ArrowRight size={16}/>
            </button>
          </div>
          {loading && <p className="text-xs text-[#6B7280] mt-2">Our estimator AI is pricing against UK build-cost benchmarks and rendering your extension — up to a minute.</p>}
        </div>
      </Section>
    );
  }

  // ---------- RESULT ----------
  const quote = q?.quote || {};
  return (
    <Section title="Your fixed quote" eyebrow="Step 3 · Planning">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
        <div>
          <div className="relative border border-[#E5E7EB] bg-[#F4F4F4] aspect-[3/2] overflow-hidden" data-testid="planning-render-wrap">
            {q?.render_url
              ? <img data-testid="planning-render" src={`${BACKEND}${q.render_url}`} alt="Render of your extension" className="w-full h-full object-cover" />
              : <div className="flex items-center justify-center h-full text-sm text-[#6B7280]">Render preview unavailable</div>}
          </div>
          <p className="text-xs text-[#6B7280] mt-2">AI-generated render — indicative of the finished extension.</p>
          <div className="bg-[#12263A] text-white p-6 mt-4">
            <div className="text-xs uppercase tracking-widest text-[#C79A3B]">Fixed quote (incl. VAT)</div>
            {q?.discount_amount > 0 && (
              <div className="text-sm text-white/60 line-through" data-testid="planning-quote-before">£{Math.round(q?.total_before_discount || 0).toLocaleString("en-GB")}</div>
            )}
            <div className="text-4xl font-black mt-1" data-testid="planning-quote-total">£{Math.round(q?.total_inc_vat || 0).toLocaleString("en-GB")}</div>
            {q?.discount_amount > 0 && (
              <div className="text-xs font-bold text-[#C79A3B] mt-2" data-testid="planning-discount-label">{q.discount_label} · save £{Math.round(q.discount_amount).toLocaleString("en-GB")}</div>
            )}
          </div>
          {q?.reference && (
            <div className="border border-[#E5E7EB] bg-white p-3 mt-4 text-sm" data-testid="planning-quote-ref">
              <span className="label-eyebrow">Quote ref</span>
              <span className="ml-2 font-black text-[#C79A3B]">{q.reference}</span>
              {q?.valid_until && <span className="text-xs text-[#6B7280] ml-2">· valid until <b>{new Date(q.valid_until).toLocaleDateString("en-GB", { day: "2-digit", month: "long", year: "numeric" })}</b></span>}
            </div>
          )}
          <div className="flex gap-3 mt-6">
            <button data-testid="planning-restart" className="de-btn-secondary" onClick={() => { setPhase("upload"); setQ(null); }}>Start over</button>
            <button data-testid="planning-proceed" className="de-btn-primary" onClick={() => onProceed({ quoteId: q.quote_id, total: q.total_inc_vat, name, email, mobile })}>
              Proceed to book <ArrowRight size={16}/>
            </button>
          </div>
        </div>

        <div data-testid="planning-quote-detail">
          {quote.summary && <p className="text-[#12263A] mb-4">{quote.summary}</p>}
          <QuoteList title="Scope of works" items={quote.scope_of_works} testid="quote-scope" />
          <QuoteList title="Preliminaries" items={quote.preliminaries} testid="quote-prelims" />
          <QuoteList title="What's included" items={quote.inclusions} testid="quote-inclusions" />
          <QuoteList title="Not included" items={quote.exclusions} testid="quote-exclusions" />
          <p className="text-xs text-[#6B7280] mt-4">Fixed price based on your approved plans and answers; final dimensions confirmed at survey.</p>
        </div>
      </div>
    </Section>
  );
}
